import {
  Button,
  Label,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
  RadioGroup,
  RadioGroupItem,
} from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "../../../../../convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "../../../../../convex/schema";
import {
  isBackendError,
  type AutodiagReponse,
  type AutodiagReponses,
  type FormTemplateProps,
} from "../../lib/types";
import {
  AUTODIAG_QUESTIONS_PER_PAGE,
  autodiagOptions,
  autodiagQuestions,
} from "./autodiag-data";

// Build Zod schema from questions
const schema = z.object(
  Object.fromEntries(
    autodiagQuestions
      .flatMap((q) => [q, ...(q.sous_questions || [])])
      .map((q) => [
        q.name,
        z.string({ required_error: "Reponse requise." }).min(1),
      ]),
  ),
);

type FormData = z.infer<typeof schema>;

export function CandidateAutodiagTemplate({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const {
    currentStep,
    setCurrentStep,
    setStartTime,
    isSubmitting,
    setIsSubmitting,
    reset,
  } = useFormStore();

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const totalPages = Math.ceil(
    autodiagQuestions.length / AUTODIAG_QUESTIONS_PER_PAGE,
  );

  // Initialize all fields with empty strings to avoid uncontrolled->controlled warning
  const allQuestions = autodiagQuestions.flatMap((q) => [
    q,
    ...(q.sous_questions || []),
  ]);
  const initialDefaults = Object.fromEntries(
    allQuestions.map((q) => [q.name, ""]),
  ) as FormData;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: (
      Object.values(evaluation.donnees.reponses) as AutodiagReponse[]
    ).reduce(
      (acc: FormData, q) => ({
        ...acc,
        [q.name]: q.reponse,
        ...q.sous_questions?.reduce(
          (subAcc: FormData, sq) => ({
            ...subAcc,
            [sq.name]: sq.reponse,
          }),
          {},
        ),
      }),
      initialDefaults,
    ) as FormData,
  });

  useEffect(() => {
    reset();
    setStartTime(Date.now());
    return () => {
      reset();
    };
  }, [reset, setStartTime]);

  // Build donnees object for backend
  const buildDonnees = (data: FormData) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.AUTODIAG,
    reponses: autodiagQuestions.reduce<AutodiagReponses>(
      (acc, q) => ({
        ...acc,
        [q.name]: {
          titre: q.titre,
          name: q.name,
          label: q.label,
          reponse: data[q.name as keyof FormData] as string,
          sous_questions: q.sous_questions?.map((sq) => ({
            name: sq.name,
            label: sq.label,
            reponse: data[sq.name as keyof FormData] as string,
          })),
        },
      }),
      {},
    ),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const startIndex = currentStep * AUTODIAG_QUESTIONS_PER_PAGE;
  const currentQuestion = autodiagQuestions[startIndex];

  if (!currentQuestion) {
    return <div>Chargement...</div>;
  }

  const validateCurrentPage = async () => {
    const currentFields = [
      currentQuestion.name,
      ...(currentQuestion.sous_questions?.map((q) => q.name) || []),
    ];
    return await form.trigger(currentFields);
  };

  const handleNext = async () => {
    const isValid = await validateCurrentPage();
    if (isValid) {
      await goToPage(currentStep + 1, form.getValues());
    }
  };

  const progressPercentage = ((currentStep + 1) / totalPages) * 100;

  const goToPage = async (page: number, data: FormData) => {
    if (page >= 0 && page < totalPages) {
      console.log(data);
      await saveResponses(data);
      setCurrentStep(page);
    }
  };

  const isFirstPage = currentStep === 0;
  const isLastPage = currentStep === totalPages - 1;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mx-auto container py-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">Autodiagnostic</h1>
          <p className="text-muted-foreground">
            Test d'auto diagnostic - 60 questions
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              Page {currentStep + 1} sur {totalPages}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div className="space-y-4">
          {currentQuestion.sous_questions &&
            currentQuestion.sous_questions.map((q) => (
              <FormField
                key={q.name}
                control={form.control}
                name={q.name}
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between p-4">
                    <Label
                      htmlFor={q.name}
                      className="flex lg:w-[50%] cursor-pointer pr-4"
                    >
                      <div className="flex flex-col">
                        {q.label}
                        <FormMessage />
                      </div>
                    </Label>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={!editable}
                        className="w-fit flex flex-row flex-1 justify-evenly"
                      >
                        {autodiagOptions.map((option) => (
                          <div
                            className="flex flex-col items-center gap-3"
                            key={option.value}
                          >
                            <RadioGroupItem
                              value={option.value.toString()}
                              id={`option-${q.name}-${option.value}`}
                            />
                            <Label htmlFor={`option-${q.name}-${option.value}`}>
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
          <FormField
            key={currentQuestion.name}
            control={form.control}
            name={currentQuestion.name}
            render={({ field }) => (
              <FormItem className="flex items-center justify-between p-4 rounded-lg bg-primary/5">
                <Label
                  htmlFor={currentQuestion.name}
                  className="flex lg:w-[50%] cursor-pointer pr-4"
                >
                  <div className="flex flex-col">
                    <span>{currentQuestion.label}</span>
                    <FormMessage />
                  </div>
                </Label>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!editable}
                    className="w-fit flex flex-row flex-1 justify-evenly"
                  >
                    {autodiagOptions.map((option) => (
                      <div
                        className="flex flex-col items-center gap-3"
                        key={option.value}
                      >
                        <RadioGroupItem
                          value={option.value.toString()}
                          id={`option-main-${option.value}`}
                        />
                        <Label htmlFor={`option-main-${option.value}`}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => goToPage(currentStep - 1, form.getValues())}
            disabled={isFirstPage}
            className="flex-1"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Precedent
          </Button>

          {isLastPage ? (
            <Button type="submit" className="flex-1" disabled={isSubmitting || !editable}>
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button type="button" onClick={handleNext} className="flex-1">
              Suivant
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
